import Guid from "common/values/guid/guid";
import {Change, diffLines, diffWords} from "diff";
import {ProposalField, ProposalFieldJSON} from "work/entities/proposal/proposal";
import TextChange, {
  TextChangeCollection,
  TextChangeCollectionJSON,
} from "work/entities/proposal/redlining/_diff/text-change";
import {RedlineAction, RedlineChange, RedlineChangeJSON,} from "work/entities/proposal/redlining/redline-change";
import {HumanReadableProposalFieldName} from "work/values/constants";

export interface IRedlineableField {
  isEmpty: boolean;
  isEqualTo(other: unknown): boolean;
  fromJSON(obj: object | string | boolean | number | null): IRedlineableField | null;
  toJSON(): object | string | boolean | number | null;
  clone(): IRedlineableField;
  replacesId?: Guid;
}

export interface IComparableFieldEntryWithId extends IRedlineableField {
  id: Guid;
}

export type RedlineSubField = {
  textChangeCollection: TextChangeCollection;
  diffWords: boolean;
};

export interface FieldRedlineJSON {
  _field: ProposalFieldJSON;
  _originalEntry: string | number | boolean | object | null;
  _revisedEntry: string | number | boolean | object | null;
  _currentEntry: string | number | boolean | object | null | undefined;
  _sessionHistory: RedlineChangeJSON[];
  _diffWords: boolean;
  _textChangeCollection: TextChangeCollectionJSON;
}

export interface FieldRedlineArrayJSON {
  _field: ProposalFieldJSON;
  _redlines: FieldRedlineJSON[];
  _sessionHistory: RedlineChangeJSON[];
}

export default class FieldRedline<T extends IRedlineableField> {
  private readonly _prototype: T;
  private readonly _field: ProposalField;
  private readonly _originalEntry: T | null;
  private readonly _revisedEntry: T | null;
  private _currentEntry: T | null | undefined;
  protected _sessionHistory: RedlineChange[] = [];
  private readonly _diffWords: boolean;
  private _originalRedline: FieldRedline<T>;

  private _textChangeCollection: TextChangeCollection =
    new TextChangeCollection();
  private readonly _subFieldTextChangeCollection: Map<
    keyof T,
    RedlineSubField
  > = new Map();

  constructor(
    prototype: T,
    field: ProposalField,
    originalEntry: T | null,
    revisedEntry: T | null,
    changes?: TextChangeCollection,
    diffWords: boolean = false,
    originalRedline?: FieldRedline<T>
  ) {
    this._prototype = prototype;
    this._field = field;
    this._originalEntry = originalEntry;
    this._revisedEntry = revisedEntry;
    this._diffWords = diffWords;
    this._originalRedline = originalRedline ?? this;

    if (changes) {
      this._textChangeCollection = changes;
    } else {
      this.updateDiff();
    }
    this.resetCurrentEntry();
  }

  public get label(): string {
    return HumanReadableProposalFieldName[this._field.name];
  }

  public get field(): ProposalField {
    return this._field;
  }

  public get originalEntry(): T | null {
    return this._originalEntry;
  }

  public get revisedEntry(): T | null {
    return this._revisedEntry;
  }

  public get currentEntry(): T | null | undefined {
    return this._currentEntry;
  }

  public get changes(): TextChange[] {
    return this._textChangeCollection.changes;
  }

  public get originalRedline(): FieldRedline<T> {
    return this._originalRedline;
  }

  public set originalRedline(original: FieldRedline<T>) {
    this._originalRedline = original;
  }

  set changes(newChanges: TextChange[]) {
    this._textChangeCollection.changes = newChanges;
  }

  public get textChangeCollection(): TextChangeCollection {
    return this._textChangeCollection;
  }

  public get sessionHistory(): RedlineChange[] {
    return this._sessionHistory;
  }

  public get isAdded(): boolean {
    return (
      (this.originalEntry === null && this.revisedEntry !== null) ||
      (this.revisedEntry === null && !!this.currentEntry)
    );
  }

  public get isNewlyAdded(): boolean {
    return Boolean(
      this._currentEntry && !this.wasRedlined && !this._revisedEntry
    );
  }

  public get isRemoved(): boolean {
    if (this.currentEntry) return false;
    return (
      (this.revisedEntry !== null && this.currentEntry === null) ||
      (this.originalEntry !== null && this.revisedEntry === null)
    );
  }

  public get isNewlyRemoved(): boolean {
    return this.revisedEntry !== null && this.currentEntry === null;
  }

  public get isRevised(): boolean {
    return this._currentEntry !== undefined && !this.isAccepted;
  }

  public get isNewlyRevised(): boolean {
    return Boolean(
      this._currentEntry && !this._revisedEntry?.isEqualTo(this._currentEntry)
    );
  }

  public get hasChanges(): boolean {
    return this.isNewlyRevised || this.isRejected || this.isNewlyAdded || this.isNewlyRemoved;
  }

  public get wasRedlined(): boolean {
    if (this._originalEntry === null && this._revisedEntry === null) {
      return false;
    } else if (this._revisedEntry === null) {
      return true;
    } else {
      return !this.revisedEntry?.isEqualTo(this.originalEntry);
    }
  }

  public get isResolved(): boolean {
    return this._currentEntry !== undefined;
  }

  public get canBeUndone(): boolean {
    return (this.wasRedlined && this.isResolved) || this.isRevised;
  }

  public get isAccepted(): boolean {
    if (this._currentEntry === null && this._revisedEntry === null) {
      return true;
    }
    return this._currentEntry?.isEqualTo(this._revisedEntry) ?? false;
  }

  public get isRejected(): boolean {
    return this.isResolved && this.wasRedlined && !this.isAccepted;
  }

  public add(newEntry: T): FieldRedline<T> {
    const newRedline = this.clone();
    newRedline._currentEntry = newEntry;
    newRedline.updateDiff();
    newRedline._sessionHistory = [];
    const redlineChange = new RedlineChange(
      this._field,
      newRedline.isResolved,
      RedlineAction.Add,
      newRedline.changes
    );
    newRedline._sessionHistory.push(redlineChange);
    return newRedline;
  }

  public edit(newEntry: T | null, preserveSessionHistory: boolean = false): FieldRedline<T> {
    const newRedline = this.clone();
    if (
      newEntry?.isEqualTo(this._currentEntry) ||
      (newEntry === null && this._currentEntry === null)
    ) {
      return newRedline;
    }
    newRedline._currentEntry = newEntry;
    if (preserveSessionHistory) {
      return newRedline;
    }

    newRedline._sessionHistory = [];


    newRedline.updateDiff();
    if (
      (newEntry === null && this._originalRedline._currentEntry === null) ||
      (newEntry?.isEmpty && this._originalRedline._currentEntry?.isEmpty) ||
      newEntry?.isEqualTo(this._originalRedline._currentEntry)
    ) {
      return newRedline;
    }

    let action: RedlineAction;
    let changes: TextChange[] = this._originalRedline.changes;
    if (
      (!this._originalRedline.currentEntry ||
        this._originalRedline.currentEntry.isEmpty) &&
      newEntry &&
      !newEntry.isEmpty
    ) {
      action = RedlineAction.Add;
      changes = newRedline.changes;
    } else if (
      newEntry?.isEmpty &&
      this._originalRedline.currentEntry &&
      !this._originalRedline.currentEntry?.isEmpty
    ) {
      action = RedlineAction.Remove;
    } else if (
      this.revisedEntry?.isEqualTo(this._originalRedline.currentEntry)
    ) {
      action = RedlineAction.Edit;
    } else if (
      !this._originalRedline.currentEntry?.isEqualTo(
        this._originalRedline.revisedEntry
      )
    ) {
      action = RedlineAction.ReEdit;
    } else {
      action = RedlineAction.Edit;
    }

    const redlineChange = new RedlineChange(
      this._field,
      this.isResolved,
      action,
      changes
    );
    newRedline._sessionHistory.push(redlineChange);

    return newRedline;
  }

  public remove(): FieldRedline<T> {
    if (this._currentEntry === null) {
      console.warn("can't remove a redline that hasn't been modified");
      return this.clone();
    }

    const newRedline = this.clone();
    newRedline._sessionHistory = [];

    newRedline._currentEntry = null;
    newRedline.updateDiff();
    return newRedline;
  }

  public accept(): FieldRedline<T> {
    const newRedline = this.clone();
    newRedline._sessionHistory = [];

    newRedline._currentEntry = newRedline._revisedEntry;

    newRedline.updateDiff();

    if (!this._originalRedline.isAccepted) {
      const redlineChange = new RedlineChange(
        this._field,
        false,
        RedlineAction.Accept,
        this._textChangeCollection.changes
      );
      newRedline._sessionHistory.push(redlineChange);
    }
    return newRedline;
  }

  public reject(): FieldRedline<T> {
    const newRedline = this.clone();
    newRedline._sessionHistory = [];

    newRedline._currentEntry = newRedline._originalEntry;
    newRedline.updateDiff();

    if (!this._originalRedline.isRejected) {
      const redlineChange = new RedlineChange(
        this._field,
        false,
        RedlineAction.Reject,
        this._textChangeCollection.changes
      );
      newRedline._sessionHistory.push(redlineChange);
    }
    return newRedline;
  }

  public undo(): FieldRedline<T> {
    if (this._currentEntry === undefined) {
      console.warn("can't undo a redline that hasn't been modified");
      return this.clone();
    }

    const newRedline = this.clone();
    newRedline._sessionHistory = [];

    newRedline.resetCurrentEntry();
    newRedline.updateDiff();

    let action: RedlineAction | undefined;
    let changes = this._originalRedline._textChangeCollection.changes;
    let resolved = true;
    if (this._originalRedline.wasRedlined && this._originalRedline.isAccepted) {
      action = RedlineAction.UndoAccept;
      changes = this.getInitialChanges();
      resolved = false;
    } else if (
      this._originalRedline.wasRedlined &&
      this._originalRedline.isRejected
    ) {
      action = RedlineAction.UndoReject;
      changes = this.getInitialChanges();
      resolved = false;
    } else if (
      (this._originalRedline.isResolved &&
        this._originalRedline._currentEntry &&
        !this._originalRedline._currentEntry.isEmpty &&
        newRedline._currentEntry == null) ||
      newRedline.currentEntry?.isEmpty
    ) {
      action = RedlineAction.Remove;
    } else if (this._originalRedline.isResolved) {
      action = RedlineAction.UndoEdit;
      changes = this.changes;
    }

    if (action && !this._sessionHistory.length) {
      const redlineChange = new RedlineChange(
        this._field,
        resolved,
        action,
        changes
      );
      newRedline._sessionHistory.push(redlineChange);
    }
    return newRedline;
  }

  private getInitialChanges() {
    const changes = new Array<TextChange>();
    const previous = this._originalRedline._originalEntry?.toString() ?? "";
    const next = this._originalRedline._revisedEntry?.toString() ?? "";
    const diff = this._diffWords
      ? diffWords(
        previous,
        next
      )
      : diffLines(
        previous,
        next
      );

    diff.forEach((change: Change) => {
      changes.push(new TextChange(
        this._textChangeCollection,
        change
      ));
    });
    return changes;
  }

  public clearSessionHistory() {
    this._sessionHistory = [];
  }

  public isEqualTo(other?: FieldRedline<T>): boolean {
    if (!other) {
      return false;
    }
    return Boolean(
      this._field.isEqualTo(other.field) &&
      ((this._originalEntry === null && other._originalEntry === null) ||
        this._originalEntry?.isEqualTo(other.originalEntry)) &&
      ((this._revisedEntry === null && other._revisedEntry === null) ||
        this._revisedEntry?.isEqualTo(other.revisedEntry)) &&
      ((this._currentEntry === null && other._currentEntry === null) ||
        (this._currentEntry === undefined &&
          other._currentEntry === undefined) ||
        this._currentEntry?.isEqualTo(other.currentEntry))
    );
  }

  public isCurrentWith(other: FieldRedline<T>): boolean {
    if (!this._field.isEqualTo(other.field)) return false;
    if (this._originalEntry === null && other._originalEntry !== null)
      return false;
    if (
      this._originalEntry &&
      !this._originalEntry.isEqualTo(other.originalEntry)
    )
      return false;
    if (this._revisedEntry === null && other._revisedEntry !== null)
      return false;
    return !(this._revisedEntry && !this._revisedEntry.isEqualTo(other.revisedEntry));
  }

  public clone(originalRedline?: FieldRedline<T>): FieldRedline<T> {
    const clone = FieldRedline.fromJSON<T>(
      this._prototype,
      this.toJSON(),
      this._originalRedline || this
    );
    clone._originalRedline = originalRedline ?? clone._originalRedline;
    return clone;
  }

  public toJSON(): FieldRedlineJSON {
    return {
      _field: this._field.toJSON(),
      _originalEntry: this._originalEntry
        ? this._originalEntry.toJSON()
        : this._originalEntry,
      _revisedEntry: this._revisedEntry
        ? this._revisedEntry.toJSON()
        : this._revisedEntry,
      _currentEntry: this._currentEntry
        ? this._currentEntry.toJSON()
        : this._currentEntry,
      _sessionHistory: this._sessionHistory.map((change) => change.toJSON()),
      _diffWords: this._diffWords,
      _textChangeCollection: this._textChangeCollection.toJSON(),
    };
  }

  public static fromJSON<T extends IRedlineableField>(
    prototype: T,
    json: FieldRedlineJSON,
    originalRedline?: FieldRedline<T>
  ): FieldRedline<T> {
    const field = ProposalField.fromJSON(json._field);
    const originalEntry = prototype.fromJSON(json._originalEntry) as T | null;
    const revisedEntry = prototype.fromJSON(json._revisedEntry) as T | null;
    const currentEntry =
      json._currentEntry === null || json._currentEntry === undefined
        ? json._currentEntry
        : (prototype.fromJSON(json._currentEntry) as T | null);
    const sessionHistory = new Array<RedlineChange>();
    if (json._sessionHistory) {
      json._sessionHistory.forEach((change: RedlineChangeJSON) => {
        sessionHistory.push(RedlineChange.fromJSON(change));
      });
    }

    const changes = json._textChangeCollection?.changes
      ? TextChangeCollection.fromJSONs(json._textChangeCollection.changes)
      : undefined;
    if (!field) {
      throw new Error("FieldRedline.fromJSON: field is required");
    }
    const newFieldRedline = new FieldRedline<T>(
      prototype,
      field,
      originalEntry,
      revisedEntry,
      changes,
      json._diffWords,
      originalRedline
    );
    newFieldRedline._currentEntry = currentEntry;
    newFieldRedline._sessionHistory = sessionHistory;
    return newFieldRedline;
  }

  private resetCurrentEntry() {
    if (this._originalEntry === null && this.revisedEntry === null) {
      this._currentEntry = null;
    } else if (
      this._revisedEntry &&
      this._originalEntry &&
      this._revisedEntry.isEqualTo(this._originalEntry)
    ) {
      this._currentEntry = this._originalEntry;
    } else {
      this._currentEntry = undefined;
    }
  }

  public updateDiff() {
    this._textChangeCollection = new TextChangeCollection();
    if (
      this._originalEntry === null &&
      this._revisedEntry === null &&
      !this._currentEntry
    ) {
      return;
    }

    let previous: string;
    let next: string;
    if (this._currentEntry !== undefined) {
      previous = this._revisedEntry?.toString() ?? "";
      next = this._currentEntry?.toString() ?? "";
    } else {
      previous = this._originalEntry?.toString() ?? "";
      next = this._revisedEntry?.toString() ?? "";
    }

    const diff = this._diffWords
      ? diffWords(
        previous,
        next
      )
      : diffLines(
        previous,
        next
      );

    diff.forEach((change: Change) => {
      this._textChangeCollection.changes.push(
        new TextChange(
          this._textChangeCollection,
          change
        )
      );
    });

    this.updateSubFieldDiffs();
  }

  private updateSubFieldDiffs() {
    for (const [fieldName, subFieldChanges] of this
      ._subFieldTextChangeCollection) {
      const previous = this._revisedEntry?.[fieldName]?.toString() ?? "";
      const next = this._currentEntry?.[fieldName]?.toString() ?? "";
      const diff = subFieldChanges.diffWords
        ? diffWords(
          previous,
          next
        )
        : diffLines(
          previous,
          next
        );
      subFieldChanges.textChangeCollection = new TextChangeCollection();
      diff.forEach((change: Change) => {
        subFieldChanges.textChangeCollection.changes.push(
          new TextChange(
            subFieldChanges.textChangeCollection,
            change
          )
        );
      });
    }
  }
}

export class FieldRedlineArray<T extends IComparableFieldEntryWithId> {
  private readonly _prototype: T;
  private readonly _arrayField: ProposalField;
  private readonly _arrayEntryFieldConstructor: (id: Guid) => ProposalField;
  protected _sessionHistory: RedlineChange[] = [];
  protected _lastEntryUpdated: Guid | undefined;
  protected _originalRedlineArray: FieldRedlineArray<T>
  protected _redlines: FieldRedline<T>[] = [];

  constructor(
    prototype: T,
    arrayField: ProposalField,
    arrayEntryFieldConstructor: (id: Guid) => ProposalField,
    originalEntries: T[],
    revisedEntries: T[],
    originalRedlineArray?: FieldRedlineArray<T>
  ) {
    this._prototype = prototype;
    this._arrayField = arrayField;
    this._arrayEntryFieldConstructor = arrayEntryFieldConstructor;
    this._originalRedlineArray = originalRedlineArray ?? this;

    originalEntries.forEach((originalEntry) => {
      let fieldRedline: FieldRedline<T> | undefined;

      const modifiedEntry = revisedEntries.find(
        (revisedEntry: T) =>
          revisedEntry.replacesId?.isEqualTo(originalEntry.id) ||
          originalEntry.replacesId?.isEqualTo(revisedEntry.id)
      );
      if (modifiedEntry !== undefined) {
        fieldRedline = new FieldRedline<T>(
          prototype,
          this._arrayEntryFieldConstructor(originalEntry.id),
          originalEntry,
          modifiedEntry
        );
      }

      const remainingEntry = revisedEntries.find((revisedEntry: T) =>
        revisedEntry.id?.isEqualTo(originalEntry.id)
      );
      if (remainingEntry) {
        fieldRedline =
          fieldRedline ??
          new FieldRedline<T>(
            prototype,
            this._arrayEntryFieldConstructor(remainingEntry.id),
            originalEntry,
            originalEntry
          );
      }

      fieldRedline =
        fieldRedline ??
        new FieldRedline<T>(
          prototype,
          this._arrayEntryFieldConstructor(originalEntry.id),
          originalEntry,
          null
        );
      this._redlines.push(fieldRedline);
    });

    const addedEntries = revisedEntries
      .filter(
        (revisedEntry) =>
          !revisedEntry.replacesId &&
          !this._redlines.some((redline) =>
            redline.revisedEntry?.id.isEqualTo(revisedEntry.id)
          )
      )
      .filter(
        (revisedEntry) =>
          !originalEntries.find((originalEntry) =>
            originalEntry.id?.isEqualTo(revisedEntry.id)
          )
      );
    addedEntries.forEach((addedEntry) => {
      this._redlines.push(
        new FieldRedline<T>(
          prototype,
          this._arrayEntryFieldConstructor(addedEntry.id),
          null,
          addedEntry
        )
      );
    });
  }

  public get field(): ProposalField {
    return this._arrayField;
  }

  public get redlines(): FieldRedline<T>[] {
    return this._redlines;
  }

  public get originalRedlineArray(): FieldRedlineArray<T> {
    return this._originalRedlineArray;
  }

  public get sessionHistory(): RedlineChange[] {
    return [
      ...this._sessionHistory,
      ...this._redlines.flatMap((redline) => redline.sessionHistory),
    ];
  }

  public get isRevised(): boolean {
    return this._redlines.some((redline) => redline.isRevised);
  }

  public get isResolved(): boolean {
    return this._redlines.every((redline) => redline.isResolved);
  }

  public get canBeUndone(): boolean {
    return this._redlines.some((redline) => redline.canBeUndone);
  }

  public get isEmpty(): boolean {
    return this._redlines.every((redline) => redline.currentEntry === null);
  }

  public get currentIds(): Guid[] {
    return this._redlines
      .map((redline) => redline.currentEntry?.id)
      .filter((id) => id !== undefined);
  }

  public get hasRejections(): boolean {
    return this._redlines.some((redline) => redline.isRejected);
  }

  public get hasChanges(): boolean {
    for (const redline of this._redlines) {
      if (redline.hasChanges) return true;
    }
    return false;
  }

  public clearSessionHistory() {
    this._sessionHistory = [];
    this._redlines.forEach((redline) =>
      redline.clearSessionHistory()
    );
  }

  public addEntry(entry: T): FieldRedlineArray<T> {
    const newRedlineArray = this.clone();
    newRedlineArray._sessionHistory = newRedlineArray._sessionHistory.filter(
      (change) =>
        !change.field?.isEqualTo(this._arrayEntryFieldConstructor(entry.id))
    );

    const existingFieldRedline = newRedlineArray._redlines.find(
      (redline) =>
        redline.revisedEntry?.id.isEqualTo(entry.id) ||
        redline.originalEntry?.id.isEqualTo(entry.id)
    );

    if (existingFieldRedline?.currentEntry)
      throw new Error("Entry already exists in redline");

    if (
      existingFieldRedline?.revisedEntry &&
      existingFieldRedline?.originalEntry
    ) {
      // Entry was removed and added again
      newRedlineArray._redlines[
        newRedlineArray._redlines.indexOf(existingFieldRedline)
        ] = existingFieldRedline.undo();
      newRedlineArray._lastEntryUpdated = entry.id;
      return newRedlineArray;
    }
    if (existingFieldRedline?.revisedEntry) {
      // Entry addition was requested in redline
      newRedlineArray._redlines[
        newRedlineArray._redlines.indexOf(existingFieldRedline)
        ] = existingFieldRedline.accept();
      newRedlineArray._lastEntryUpdated = entry.id;
      return newRedlineArray;
    }
    if (existingFieldRedline?.originalEntry) {
      // Entry removal was requested in redline
      newRedlineArray._redlines[
        newRedlineArray._redlines.indexOf(existingFieldRedline)
        ] = existingFieldRedline.reject();
      newRedlineArray._lastEntryUpdated = entry.id;
      return newRedlineArray;
    }

    const fieldRedline = new FieldRedline<T>(
      this._prototype,
      this._arrayEntryFieldConstructor(entry.id),
      null,
      null
    );
    const newFieldRedline = fieldRedline.add(entry);
    newRedlineArray._redlines.push(newFieldRedline);
    newRedlineArray._lastEntryUpdated = entry.id;

    return newRedlineArray;
  }

  public replaceEntryById(fieldId: Guid, newEntry: T): FieldRedlineArray<T> {
    if (!fieldId) throw new Error("No id provided");
    const newRedlineArray = this.clone();
    const targetIndex = newRedlineArray._redlines.findIndex(
      (fieldRedline) =>
        fieldRedline.currentEntry?.id.isEqualTo(fieldId) ||
        fieldRedline.revisedEntry?.id.isEqualTo(fieldId) ||
        fieldRedline.originalEntry?.id.isEqualTo(fieldId)
    );
    if (targetIndex === -1) throw new Error("Entry not found");
    const targetRedline = newRedlineArray._redlines[targetIndex];
    newRedlineArray._redlines[targetIndex] = targetRedline.edit(newEntry);
    newRedlineArray._lastEntryUpdated = fieldId;
    return newRedlineArray;
  }

  public removeAll(): FieldRedlineArray<T> {
    let newRedlineArray = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id) {
        newRedlineArray = newRedlineArray.removeEntryByFieldId(
          redline.field.id
        );
      }
    }
    return newRedlineArray;
  }

  public removeEntryByFieldId(fieldId?: Guid): FieldRedlineArray<T> {
    if (!fieldId) throw new Error("No id provided");
    const newRedlineArray = this.clone();
    const targetIndex = newRedlineArray._redlines.findIndex((fieldRedline) =>
      fieldRedline.field.id?.isEqualTo(fieldId)
    );
    if (targetIndex === -1) throw new Error("Entry not found");
    const targetFieldRedline = newRedlineArray._redlines[targetIndex];

    newRedlineArray._sessionHistory = newRedlineArray._sessionHistory.filter(
      (change) =>
        !change.field?.isEqualTo(this._arrayEntryFieldConstructor(fieldId))
    );
    if (targetFieldRedline.isNewlyAdded && !targetFieldRedline.originalEntry) {
      newRedlineArray._redlines.splice(
        targetIndex,
        1
      );
    } else {
      newRedlineArray._redlines[targetIndex] = targetFieldRedline.remove();
      const redlineChange = new RedlineChange(
        this._arrayEntryFieldConstructor(fieldId),
        true,
        RedlineAction.Remove,
        targetFieldRedline.textChangeCollection.changes
      );
      newRedlineArray._sessionHistory.push(redlineChange);
    }

    newRedlineArray._lastEntryUpdated = fieldId;

    return newRedlineArray;
  }

  public acceptAll(): FieldRedlineArray<T> {
    let newRedlineArray = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id) {
        newRedlineArray = newRedlineArray.acceptRedlineById(redline.field.id);
      }
    }
    return newRedlineArray;
  }

  public acceptRedlineById(fieldId: Guid): FieldRedlineArray<T> {
    const newRedline = this.clone();
    const target = newRedline._redlines.find((redline) =>
      redline.field.id?.isEqualTo(fieldId)
    );
    if (target) {
      const index = newRedline._redlines.indexOf(target);
      newRedline._redlines[index] = target.accept();
      newRedline._lastEntryUpdated = fieldId;
    }
    return newRedline;
  }

  public rejectAll(): FieldRedlineArray<T> {
    let newRedlineArray = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id) {
        newRedlineArray = newRedlineArray.rejectRedlineById(redline.field.id);
      }
    }
    return newRedlineArray;
  }

  public rejectRedlineById(fieldId: Guid): FieldRedlineArray<T> {
    const newRedlineArray = this.clone();
    const targetIndex = newRedlineArray._redlines.findIndex((redline) =>
      redline.field.id?.isEqualTo(fieldId)
    );
    if (targetIndex === -1) throw new Error("Redline not found");
    const targetRedline = newRedlineArray._redlines[targetIndex];
    newRedlineArray._redlines[targetIndex] = targetRedline.reject();
    newRedlineArray._lastEntryUpdated = fieldId;
    return newRedlineArray;
  }

  public undoAll(): FieldRedlineArray<T> {
    let newRedlineArray = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id) {
        newRedlineArray = newRedlineArray.undoRedlineById(redline.field.id);
      }
    }
    return newRedlineArray;
  }

  public undoRedlineById(fieldId: Guid): FieldRedlineArray<T> {
    const newRedlineArray = this.clone();
    newRedlineArray._sessionHistory = newRedlineArray._sessionHistory.filter(
      (change) =>
        !change.field?.isEqualTo(this._arrayEntryFieldConstructor(fieldId))
    );
    const target = newRedlineArray._redlines.find((redline) =>
      redline.field.id?.isEqualTo(fieldId)
    );
    if (target?.isNewlyAdded && !target.originalEntry) {
      newRedlineArray._redlines.splice(
        newRedlineArray._redlines.indexOf(target),
        1
      );
      const redlineChange = new RedlineChange(
        this._arrayField,
        true,
        RedlineAction.Remove,
        target.textChangeCollection.changes
      );
      this._sessionHistory.push(redlineChange);
    } else if (target) {
      const index = newRedlineArray._redlines.indexOf(target);
      newRedlineArray._redlines[index] = target.undo();
    }
    return newRedlineArray;
  }

  public updateRedline(fieldRedline: FieldRedline<T>): FieldRedlineArray<T> {
    const newRedlineArray = this.clone();
    const targetIndex = newRedlineArray._redlines.findIndex((redline) =>
      redline.field.id?.isEqualTo(fieldRedline.field.id)
    );
    if (targetIndex === -1) {
      newRedlineArray._redlines.push(fieldRedline);
    } else {
      newRedlineArray._redlines[targetIndex] = fieldRedline;
    }
    newRedlineArray._lastEntryUpdated = fieldRedline.field.id ?? undefined;
    return newRedlineArray;
  }

  public isEqualTo(other: FieldRedlineArray<T>): boolean {
    return (
      this._arrayField.isEqualTo(other.field) &&
      this._redlines.length === other.redlines.length &&
      this._redlines.every((redline) =>
        other.redlines.some((otherRedline) => redline.isEqualTo(otherRedline))
      )
    );
  }

  /**
   * Takes an outdated redline, and merges its current values if this redline does not
   * appear to have made any changes to the field.
   *
   * This is so when an opposing party submits a revision, you do not lose your changes
   * unless it's a field they have also changed.
   * @param oldRedlineArray
   * @param userIsCreator
   */
  public mergeOldRedlineArray(
    oldRedlineArray: FieldRedlineArray<T>,
    userIsCreator: boolean
  ): FieldRedlineArray<T> {
    const newRedlineArray = this.clone();
    newRedlineArray._redlines = [];
    for (const newRedline of this._redlines) {
      const relatedRedline = oldRedlineArray._redlines.find((oldRedline) =>
        oldRedline.field.isEqualTo(newRedline.field)
      );
      if (
        relatedRedline &&
        (userIsCreator || relatedRedline.isCurrentWith(newRedline))
      ) {
        newRedlineArray._redlines.push(relatedRedline);
      } else {
        newRedlineArray._redlines.push(newRedline);
      }
    }
    for (const oldRedline of oldRedlineArray._redlines) {
      if (
        !newRedlineArray._redlines.some((newRedline) =>
          newRedline.field.isEqualTo(oldRedline.field)
        )
      ) {
        newRedlineArray._redlines.push(oldRedline);
      }
    }
    return newRedlineArray;
  }

  /**
   * Takes a redline array shared between the same party (e.g. Client Reviewers + Client) and merges based on the
   * original state of the existingRedlineArray.
   *
   * This is so when a member of the same party saves redline array changes, you do not lose your changes
   * unless it's a field they have also changed.
   * @param existingRedlineArray the existing redline to merge
   */
  public mergeSharedRedlineArray(existingRedlineArray: FieldRedlineArray<T>): FieldRedlineArray<T> {
    const newRedlineArray = new FieldRedlineArray<T>(
      this._prototype,
      this._arrayField,
      this._arrayEntryFieldConstructor,
      [],
      []
    );

    this.redlines.forEach((newRedline) => {
      const existingUpdatedRedline =
        existingRedlineArray.redlines
          .find((existingRedline) => {
            return existingRedline.field.isEqualTo((newRedline.field)) &&
              existingRedlineArray.originalRedlineArray.redlines
                .some((originalFieldRedline) => {
                  return newRedline.isEqualTo(originalFieldRedline);
                });
          });

      const wasRemovedInExisting =
        existingRedlineArray.originalRedlineArray.redlines
          .some((originalFieldRedline) => {
            return newRedline.isEqualTo(originalFieldRedline);
          }) &&
        !existingRedlineArray.redlines
          .some((existingRedline) => {
            return existingRedline.field.isEqualTo((newRedline.field))
          });

      if (existingUpdatedRedline) {
        newRedlineArray._redlines.push(existingUpdatedRedline);
      } else if (!wasRemovedInExisting) {
        newRedlineArray._redlines.push(newRedline);
      }
    })

    existingRedlineArray.redlines.forEach((existingRedline) => {
      const wasAddedInExisting = !existingRedlineArray.originalRedlineArray.redlines
        .some((originalRedline) => {
          return existingRedline.field.isEqualTo(originalRedline.field);
        });
      const wasAddedInNew = newRedlineArray.redlines
        .some((newRedline) => {
          return newRedline.field.isEqualTo(existingRedline.field)
        })
      if (wasAddedInExisting && !wasAddedInNew) {
        newRedlineArray._redlines.push(existingRedline);
      }
    });

    return newRedlineArray;
  }

  public static fromArrayObject<T extends IComparableFieldEntryWithId>(
    prototype: T,
    arrayField: ProposalField,
    arrayEntryFieldConstructor: (id: Guid) => ProposalField,
    arrayObject: FieldRedlineArrayJSON
  ): FieldRedlineArray<T> {
    const redlineArray = new FieldRedlineArray<T>(
      prototype,
      arrayField,
      arrayEntryFieldConstructor,
      [],
      []
    );
    arrayObject._redlines.forEach((fieldRedline) => {
      const redlineInstance = FieldRedline.fromJSON<T>(
        prototype,
        fieldRedline
      );
      if (redlineInstance) redlineArray._redlines.push(redlineInstance);
    });
    redlineArray._sessionHistory = new Array<RedlineChange>();
    arrayObject._sessionHistory.forEach((change) => {
      redlineArray._sessionHistory.push(RedlineChange.fromJSON(change));
    });
    return redlineArray;
  }

  public clone(): FieldRedlineArray<T> {
    const newRedlineArray = FieldRedlineArray.fromArrayObject<T>(
      this._prototype,
      this._arrayField,
      this._arrayEntryFieldConstructor,
      this.toJSON()
    );
    for (const redline of newRedlineArray._redlines) {
      redline.originalRedline =
        this._redlines.find((originalRedline) =>
          originalRedline.field.isEqualTo(redline.field)
        ) ?? redline.originalRedline;
    }
    newRedlineArray._originalRedlineArray = this._originalRedlineArray ?? newRedlineArray._originalRedlineArray;
    return newRedlineArray;
  }

  public toJSON() {
    return {
      _field: this._arrayField.toJSON(),
      _redlines: this._redlines.map((redline) => redline.toJSON()),
      _sessionHistory: this._sessionHistory.map((change) => change.toJSON()),
    };
  }
}
